// ReportPage.tsx
import 'react-calendar/dist/Calendar.css';
import Layout from '../../components/layoutConfig/layout';
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { setupAPIclient } from '../../services/setupAPIclient';
import {
  FiCheckCircle,
  FiClock,
  FiUsers,
  FiFileText,
  FiMapPin,
  FiDownload,
  FiExternalLink
} from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";
import 'animate.css';
import LocalStorage from '../../helpers/localstorage';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// ------------------ Tipagens -------------------
interface ManagementHistory {
  id: number;
  uploaddoc_id: number;
  data_vencimento: string;
  upload_at: string;
  created_at: string;
  caminho_doc?: string | null;
  updated_at: string;
}

interface Reminder {
  id: number;
  sms: boolean;
  whatts: boolean;
  email: boolean;
  message: string;
  reminder_days: number;
}

interface UserAccess {
  id: number;
  can_view: boolean;
  management_user: {
    id: number;
    email: string;
    name: string;
    number_phone: string;
  };
}

interface Company {
  id: number;
  name_company: string;
}

interface Region {
  id: number;
  name: string;
}

interface Unit {
  id: number;
  name: string;
  region_id: number;
}

interface ManagementDocumentAccess {
  id: number;
  company_id?: number;
  region_id?: number;
  unit_id?: number;
  management_location: {
    id: number;
    name: string;
    management_unit: Unit;
  };
  management_company: Company;
  management_region: Region;
  management_unit: Unit;
}

interface ManagementDoc {
  id: number;
  name: string;
  caminho_doc?: string | null;
  data_vencimento: string;
  created_at: string;
  updated_at: string;
  management_history: ManagementHistory[];
  management_uploaddoc_reminder?: Reminder[];
  management_user_uploaddoc_access: UserAccess[];
  management_document_access: ManagementDocumentAccess[];
}

// ------------------ Component -------------------
const safeFileName = (path?: string | null) =>
  path ? path.split("/").pop() || path : "Sem arquivo";

// Função auxiliar para comparar datas **sem considerar fuso horário**
const parseDateWithoutTimezone = (dateStr: string) => {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  return new Date(year, month - 1, day);
};

function ReportPage() {
  const [contracts, setContracts] = useState<ManagementDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState<ManagementDoc | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  // Buscar contratos
  const fetchContracts = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.post(`/management/get/dashboard`);
      const docs = Array.isArray(response.data.docs) ? response.data.docs : [];
      setContracts(docs);
    } catch (error) {
      console.error("Erro ao carregar contratos:", error);
      toast.error("Erro ao carregar contratos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const today = parseDateWithoutTimezone(new Date().toISOString());

  // ------------------ Separar Vigentes e Concluídos -------------------
  const contractsCurrent = contracts.filter((c) => {
    const dueDate = parseDateWithoutTimezone(c.data_vencimento);
    return dueDate >= today;
  });

  const contractsCompleted = contracts.filter((c) => {
    const dueDate = parseDateWithoutTimezone(c.data_vencimento);
    return dueDate < today;
  });

  // ------------------ Função PDF -------------------
  const generateMergedPdfSingle = async (contract: ManagementDoc) => {
  if (!contract) return;
  setGeneratingPdf(true);

  try {
    // =============== Configuração inicial ===============
    const mergedPdf = await PDFDocument.create();
    const font = await mergedPdf.embedFont(StandardFonts.Helvetica);
    const titleColor = rgb(231 / 255, 76 / 255, 60 / 255);
    const black = rgb(0, 0, 0);

    const sanitizeForWinAnsi = (v?: any) => {
      if (v === null || v === undefined) return "-";
      return String(v).replace(/[^\x00-\xFF]/g, "").trim() || "-";
    };

    const safeDate = (d?: string | null) =>
      d && !isNaN(Date.parse(d)) ? new Date(d).toLocaleDateString("pt-BR") : "-";

    const safeTime = (d?: string | null) =>
      d && !isNaN(Date.parse(d))
        ? new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
        : "-";

    // =============== Página inicial ===============
    let page = mergedPdf.addPage([595, 842]);
    let { width, height } = page.getSize();
    let cursorY = height - 50;
    const lineHeight = 20;

    const drawSectionTitle = (text: string) => {
      page.drawText(sanitizeForWinAnsi(text), {
        x: 50,
        y: cursorY,
        size: 14,
        font,
        color: titleColor,
      });
      cursorY -= lineHeight;
    };

    const newPage = () => {
      page = mergedPdf.addPage([595, 842]);
      cursorY = 800;
    };

    const checkNewPage = () => {
      if (cursorY < 70) newPage();
    };

    // ================= Cabeçalho =================
    page.drawRectangle({
      x: 0,
      y: height - 80,
      width,
      height: 80,
      color: titleColor,
    });

    const title = "Gestão de Contratos BIM";
    page.drawText(title, {
      x: (width - font.widthOfTextAtSize(title, 24)) / 2,
      y: height - 50,
      size: 24,
      font,
      color: rgb(1, 1, 1),
    });

    page.drawText(`Contrato #${sanitizeForWinAnsi(contract.id)}`, {
      x: 50,
      y: height - 70,
      size: 14,
      font,
      color: rgb(1, 1, 1),
    });

    cursorY -= 100;

    // ================= Dados =================
    drawSectionTitle("Dados do Contrato");

    page.drawText(`Nome: ${sanitizeForWinAnsi(contract.name)}`, {
      x: 60,
      y: cursorY,
      size: 12,
      font,
      color: black,
    });
    cursorY -= lineHeight;

    page.drawText(`Vencimento: ${sanitizeForWinAnsi(safeDate(contract.data_vencimento))}`, {
      x: 60,
      y: cursorY,
      size: 12,
      font,
      color: black,
    });
    cursorY -= lineHeight;

    page.drawText(`Criado em: ${sanitizeForWinAnsi(safeTime(contract.created_at))}`, {
      x: 60,
      y: cursorY,
      size: 12,
      font,
      color: black,
    });
    cursorY -= lineHeight * 1.5;

    // ================= Signatários =================
    drawSectionTitle("Signatários");

    for (const u of contract.management_user_uploaddoc_access || []) {
      const user = u?.management_user ?? {};
      page.drawText(
        `- ${sanitizeForWinAnsi(user.name)} (${sanitizeForWinAnsi(user.email)}, ${sanitizeForWinAnsi(user.number_phone)})`,
        { x: 60, y: cursorY, size: 12, font, color: black }
      );
      cursorY -= lineHeight;
      checkNewPage();
    }

    cursorY -= lineHeight / 2;

    // ================= ACESSO ORGANIZACIONAL =================
    drawSectionTitle("Acesso Organizacional (Empresa / Região / Unidade / Local)");

    for (const acc of contract.management_document_access || []) {
      const company = acc.management_company?.name_company || "-";
      const region = acc.management_region?.name || "-";
      const unit = acc.management_unit?.name || "-";
      const location = acc.management_location?.name || "-";

      page.drawText(`Empresa: ${sanitizeForWinAnsi(company)}`, {
        x: 60,
        y: cursorY,
        size: 12,
        font,
        color: black,
      });
      cursorY -= lineHeight;

      page.drawText(`Região: ${sanitizeForWinAnsi(region)}`, {
        x: 60,
        y: cursorY,
        size: 12,
        font,
        color: black,
      });
      cursorY -= lineHeight;

      page.drawText(`Unidade: ${sanitizeForWinAnsi(unit)}`, {
        x: 60,
        y: cursorY,
        size: 12,
        font,
        color: black,
      });
      cursorY -= lineHeight;

    

      checkNewPage();
    }

    cursorY -= lineHeight / 2;

    // ================= Histórico =================
    drawSectionTitle("Histórico de Uploads");

    for (const doc of contract.management_history || []) {
      try {
        const fileName = safeFileName(doc.caminho_doc);
        if (!fileName) continue;

        const createdTime = safeTime(doc.created_at);
        const vencimento = safeDate(doc.data_vencimento);

        page.drawText(`- ${sanitizeForWinAnsi(fileName)}`, {
          x: 60,
          y: cursorY,
          size: 12,
          font,
          color: black,
        });
        cursorY -= lineHeight;

        page.drawText(
          `  Criado: ${createdTime} | Vencimento: ${vencimento}`,
          { x: 80, y: cursorY, size: 12, font, color: black }
        );
        cursorY -= lineHeight * 1.2;
        checkNewPage();

        const encoded = encodeURIComponent(fileName);
        const url = `https://alexios.com.br:9598/uploads/managementDoc/${encoded}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error("Arquivo não encontrado");

        const bytes = await res.arrayBuffer();
        const buffer = new Uint8Array(bytes);

        const isPdf = buffer[0] === 0x25 && buffer[1] === 0x50 && buffer[2] === 0x44 && buffer[3] === 0x46;
        const isPng = buffer[0] === 0x89 && buffer[1] === 0x50 && buffer[2] === 0x4e && buffer[3] === 0x47;
        const isJpg = buffer[0] === 0xff && buffer[1] === 0xd8;

        if (isPdf) {
          const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true });
          const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
          pages.forEach((p) => mergedPdf.addPage(p));
        } 
        else if (isPng) {
          const img = await mergedPdf.embedPng(bytes);
          const imgPage = mergedPdf.addPage([595, 842]);
          const scale = Math.min(500 / img.width, 700 / img.height);

          imgPage.drawImage(img, {
            x: (595 - img.width * scale) / 2,
            y: (842 - img.height * scale) / 2,
            width: img.width * scale,
            height: img.height * scale,
          });
        } 
        else if (isJpg) {
          const img = await mergedPdf.embedJpg(bytes);
          const imgPage = mergedPdf.addPage([595, 842]);
          const scale = Math.min(500 / img.width, 700 / img.height);

          imgPage.drawImage(img, {
            x: (595 - img.width * scale) / 2,
            y: (842 - img.height * scale) / 2,
            width: img.width * scale,
            height: img.height * scale,
          });
        } 
        else {
          console.warn("Arquivo não é PDF nem imagem real:", fileName);
        }

      } catch (err) {
        console.warn("Falha ao processar arquivo (continua):", err);
      }
    }

    // ================= Finalização =================
    const pdfBytes = await mergedPdf.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const pdfUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `Relatorio_Contrato_${sanitizeForWinAnsi(contract.id)}.pdf`;
    a.click();
    URL.revokeObjectURL(pdfUrl);

    toast.success("PDF gerado com sucesso!");

  } catch (err: any) {
    console.error("Erro ao gerar PDF:", err);
    toast.error(`Erro ao gerar PDF: ${err?.message ?? err}`);
  } finally {
    setGeneratingPdf(false);
  }
};










  const firstLetterBadge = (email: string) => email?.trim()[0]?.toUpperCase() || "?";

  // ------------------ COLUNAS -------------------
  const columns: TableColumn<ManagementDoc>[] = [
    {
      name: "Contrato",
      selector: (row) => row.name || `#${row.id}`,
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FiFileText className="text-primary" />
          <button
            className="btn btn-link p-0 fw-semibold text-truncate"
            style={{ maxWidth: 150 }}
            onClick={() => setSelectedContract(row)}
          >
            {row.name || `#${row.id}`}
          </button>
        </div>
      ),
    },
    {
      name: "Vencimento",
      selector: (row) => row.data_vencimento,
      cell: (row) => {
        const due = parseDateWithoutTimezone(row.data_vencimento);
        const diff = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        const cls =
          diff < 0
            ? "bg-danger text-white"
            : diff <= 7
            ? "bg-warning text-dark"
            : "bg-success text-white";
        return (
          <span className={`badge px-2 py-1 ${cls}`}>
            {due.toLocaleDateString("pt-BR")}
          </span>
        );
      },
    },
    {
      name: "Signatários",
      selector: (row) =>
        row.management_user_uploaddoc_access
          .map((u) => u.management_user.email)
          .join(", "),
      cell: (row) => (
        <div className="d-flex flex-wrap gap-2">
          {row.management_user_uploaddoc_access.map((u) => (
            <div
              key={u.id}
              title={u.management_user.email}
              className="d-flex align-items-center gap-1"
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  background: "#e9f5ff",
                  color: "#0b6fb3",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                {firstLetterBadge(u.management_user.email)}
              </div>
              <small>{u.management_user.email}</small>
            </div>
          ))}
        </div>
      ),
      grow: 2,
    },
    {
      name: "Ações",
      cell: (row) => (
        <div className="d-flex flex-column flex-sm-row gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setSelectedContract(row)}
            style={{background:"#5dade2", color:"white"}}

          >
            <FiExternalLink />
          </button>
          <button
            className="btn btn-sm btn-danger"
            onClick={() => generateMergedPdfSingle(row)}
            disabled={generatingPdf}
          >
            <FiDownload className="me-1" />
            {generatingPdf ? "Gerando..." : ""}
          </button>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  return (
    <Layout>
      <div className="row py-1">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2
            className="mb-0 animate__animated animate__fadeInDown fw-bold mr-2"
            style={{ color: "#5dade2" }}
          >
            Relatórios de Contratos
          </h2>
        </div>
        <hr />

        <div className="row">
          <div className="col-md-6 mb-5">
            <div className="d-flex align-items-center mb-3">
              <FiClock size={28} className="me-2" style={{ color: "#5dade2" }} />
              <h4 className="mb-0 fw-bold" style={{ color: "#5dade2" }}>
                Vigentes
              </h4>
            </div>
            <div className="card shadow rounded-4 p-3" style={{ height: "480px" }}>
              <DataTable
                columns={columns}
                data={contractsCurrent}
                progressPending={loading}
                pagination
                highlightOnHover
                striped
                responsive
                dense
                fixedHeader
                fixedHeaderScrollHeight="380px"
              />
            </div>
          </div>

          <div className="col-md-6 mb-5">
            <div className="d-flex align-items-center mb-3">
              <FiCheckCircle size={28} className="me-2" style={{ color: "#5dade2" }} />
              <h4 className="mb-0 fw-bold" style={{ color: "#5dade2" }}>
                Vencidos
              </h4>
            </div>
            <div className="card shadow rounded-4 p-3" style={{ height: "480px" }}>
              <DataTable
                columns={columns}
                data={contractsCompleted}
                progressPending={loading}
                pagination
                highlightOnHover
                striped
                responsive
                dense
                fixedHeader
                fixedHeaderScrollHeight="380px"
              />
            </div>
          </div>
        </div>

        {selectedContract && (
          <div className="modal fade show d-block" tabIndex={-1}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg rounded-4 border-0 animate__animated animate__fadeIn">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title fw-bold text-white">
                   Visualização do contrato #{selectedContract?.id ?? "-"}
                  </h5>
                  <button
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedContract(null)}
                  />
                </div>

                <div className="modal-body">
                  <div className="row">
                    {/* ==================== COLUNA ESQUERDA ==================== */}
                    <div className="col-md-6">
                   

                      <h6 className="fw-bold">
                        <FiClock className="me-2" />
                        Vencimento
                      </h6>
                      <p>
                        {selectedContract?.data_vencimento
                          ? parseDateWithoutTimezone(selectedContract.data_vencimento).toLocaleDateString("pt-BR")
                          : "-"}
                      </p>

                      <h6 className="fw-bold">
                        <FiUsers className="me-2" />
                        Signatários
                      </h6>
                      <div className="d-flex flex-column gap-2">
                        {(selectedContract?.management_user_uploaddoc_access ?? []).map((u) => {
                          const email = u?.management_user?.email ?? "-";
                          return (
                            <div key={u?.id ?? Math.random()} className="d-flex align-items-center gap-3">
                              <div
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 18,
                                  background: "#e9f5ff",
                                  color: "#0b6fb3",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 700,
                                }}
                              >
                                {firstLetterBadge(email)}
                              </div>
                              <div>
                                <div className="fw-semibold">{email}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* ==================== COLUNA DIREITA ==================== */}
                    <div className="col-md-6">
                      <h6 className="fw-bold">
                        <FiFileText className="me-2" />
                        Histórico de uploads
                      </h6>
                      <ul className="list-group list-group-flush mb-3">
                        {(selectedContract?.management_history ?? []).map((h) => {
                          const fileName = safeFileName(h?.caminho_doc ?? "-");
                          const vencimento = h?.data_vencimento
                            ? parseDateWithoutTimezone(h.data_vencimento).toLocaleDateString("pt-BR")
                            : "-";
                          const uploadDate = h?.upload_at
                            ? new Date(h.upload_at).toLocaleString("pt-BR")
                            : "-";
                          const fileUrl = h?.caminho_doc
                            ? `https://alexios.com.br:9598/${h.caminho_doc}`
                            : "#";

                          return (
                            <li
                              key={h?.id ?? Math.random()}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <div className="fw-semibold">{fileName}</div>
                                <small className="text-muted">
                                  Venc.: {vencimento} • Upload: {uploadDate}
                                </small>
                              </div>
                              {h?.caminho_doc && (
                                <a
                                  className="btn btn-sm btn-outline-primary"
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <FiExternalLink className="me-1" /> Abrir
                                </a>
                              )}
                            </li>
                          );
                        })}
                      </ul>

                      <h6 className="fw-bold">
                        <FiMapPin className="me-2" />
                          Acessos
                      </h6>
                      <div className="d-flex flex-column gap-3">
                        {(selectedContract?.management_document_access ?? []).map((l) => {
                          const loc = l?.management_location?.name ?? "-";
                          const unit = l?.management_unit?.name ?? "-";
                          const region = l?.management_region?.name ?? "-";
                          const company = l?.management_company?.name_company ?? "-";

                          return (
                            <div key={l?.id ?? Math.random()} className="border rounded p-2">
                              <div className="fw-semibold">{loc}</div>
                              <small className="text-muted">
                                Unidade: {unit} / Região: {region} / Empresa: {company}
                              </small>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* ==================== BOTÕES ==================== */}
                  <div className="mt-4 d-flex gap-2">
                    <button
                      className="btn"
                      style={{background:"#5dade2", color:"white"}}

                      onClick={() => generateMergedPdfSingle(selectedContract)}
                      disabled={generatingPdf}
                    >
                      <FiDownload className="me-2" />{" "}
                      {generatingPdf ? "Gerando..." : "Gerar PDF"}
                    </button>
                    {selectedContract?.caminho_doc && (
                      <a
                        className="btn btn-outline-secondary"
                        href={`https://alexios.com.br:9598/${selectedContract.caminho_doc}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <FiExternalLink className="me-2" /> Abrir Documento Principal
                      </a>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedContract(null)}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        
      </div>
    </Layout>
  );
}

export default ReportPage;
